import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiClock, FiFacebook, FiInstagram, FiTwitter, FiLinkedin } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone',
      info: '+1 (555) 123-4567',
      link: 'tel:+15551234567'
    },
    {
      icon: FiMail,
      title: 'Email',
      info: 'info@eliteinterior.com',
      link: 'mailto:info@eliteinterior.com'
    },
    {
      icon: FiMapPin,
      title: 'Address',
      info: '123 Design Street, City, State 12345',
      link: '#'
    },
    {
      icon: FiClock,
      title: 'Business Hours',
      info: 'Mon-Fri: 9:00 AM - 6:00 PM',
      link: '#'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - Elite Interior Design | Get Free Consultation</title>
        <meta name="description" content="Contact Elite Interior Design for a free consultation. Call us at (555) 123-4567 or email info@eliteinterior.com. Located at 123 Design Street, City, State." />
        <meta name="keywords" content="contact interior designer, free consultation, interior design quote, design services contact" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl font-bold text-white mb-6">Contact Us</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Ready to transform your space? Get in touch with us for a free consultation. 
                We'd love to hear about your project and help bring your vision to life.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gray-700 p-6 rounded-lg text-center hover:bg-gray-600 transition-colors duration-300"
                >
                  <SafeIcon icon={item.icon} className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  {item.link !== '#' ? (
                    <a 
                      href={item.link}
                      className="text-gray-300 hover:text-yellow-400 transition-colors duration-300"
                    >
                      {item.info}
                    </a>
                  ) : (
                    <p className="text-gray-300">{item.info}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & Map */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl font-bold text-white mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors duration-300"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors duration-300"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                        Subject *
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors duration-300"
                      >
                        <option value="">Select a subject</option>
                        <option value="residential">Residential Design</option>
                        <option value="commercial">Commercial Design</option>
                        <option value="consultation">Free Consultation</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 transition-colors duration-300"
                      placeholder="Tell us about your project..."
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg transition-colors duration-300"
                  >
                    Send Message
                  </button>
                </form>
              </motion.div>

              {/* Map & Additional Info */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">Visit Our Studio</h2>
                  <div className="bg-gray-800 rounded-lg p-6">
                    <img
                      src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
                      alt="Our Studio"
                      className="w-full h-64 object-cover rounded-lg mb-4"
                    />
                    <p className="text-gray-300 mb-4">
                      Visit our design studio to see our work up close and discuss your project 
                      with our team. We have a showroom featuring the latest in furniture, 
                      materials, and design trends.
                    </p>
                    <div className="space-y-2">
                      <p className="text-white font-semibold">Studio Hours:</p>
                      <p className="text-gray-300">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-300">Saturday: 10:00 AM - 4:00 PM</p>
                      <p className="text-gray-300">Sunday: By Appointment Only</p>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="bg-gray-800 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Follow Us</h3>
                  <p className="text-gray-300 mb-4">
                    Stay connected and get inspired by following us on social media for the 
                    latest design trends and project updates.
                  </p>
                  <div className="flex space-x-4">
                    <a
                      href="#"
                      className="bg-gray-700 p-3 rounded-lg text-gray-300 hover:text-yellow-400 hover:bg-gray-600 transition-colors duration-300"
                    >
                      <SafeIcon icon={FiFacebook} className="h-5 w-5" />
                    </a>
                    <a
                      href="#"
                      className="bg-gray-700 p-3 rounded-lg text-gray-300 hover:text-yellow-400 hover:bg-gray-600 transition-colors duration-300"
                    >
                      <SafeIcon icon={FiInstagram} className="h-5 w-5" />
                    </a>
                    <a
                      href="#"
                      className="bg-gray-700 p-3 rounded-lg text-gray-300 hover:text-yellow-400 hover:bg-gray-600 transition-colors duration-300"
                    >
                      <SafeIcon icon={FiTwitter} className="h-5 w-5" />
                    </a>
                    <a
                      href="#"
                      className="bg-gray-700 p-3 rounded-lg text-gray-300 hover:text-yellow-400 hover:bg-gray-600 transition-colors duration-300"
                    >
                      <SafeIcon icon={FiLinkedin} className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-300 max-w-2xl mx-auto">
                Here are some common questions we receive from our clients.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-lg font-bold text-white mb-3">How long does a typical project take?</h3>
                <p className="text-gray-300">
                  Project timelines vary depending on scope and complexity. Residential projects 
                  typically take 4-8 weeks, while commercial projects may take 8-16 weeks.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-lg font-bold text-white mb-3">Do you offer free consultations?</h3>
                <p className="text-gray-300">
                  Yes! We offer free initial consultations to discuss your project, understand 
                  your needs, and provide you with a detailed proposal.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-lg font-bold text-white mb-3">What is your design process?</h3>
                <p className="text-gray-300">
                  Our process includes consultation, concept development, design development, 
                  implementation, and final styling. We keep you involved at every step.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-lg font-bold text-white mb-3">Do you work within specific budgets?</h3>
                <p className="text-gray-300">
                  Absolutely! We work with various budgets and will create a design plan that 
                  maximizes your investment while achieving your design goals.
                </p>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;